'use client';

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Map, { Marker, Popup, NavigationControl, MapRef } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import { MapPin, Star, ArrowRight, X } from 'lucide-react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { LANDING_COLORS } from '@/constants/landing-colors';
import { MAPBOX_TOKEN, useMapboxCss } from '@/lib/mapbox';
import { TYPE_COLORS, TYPE_ICONS, TYPE_LABELS, type DirectoryType } from '@/constants/directoryTypes';

interface Listing {
  id: string;
  name: string;
  slug: string;
  type: DirectoryType;
  city: string;
  address?: string;
  province?: string;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number | null;
  review_count?: number;
  is_claimed?: boolean;
  is_premium?: boolean;
  is_promoted?: boolean;
  observations?: string | null;
  logo_url?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  instagram?: string | null;
  facebook?: string | null;
  view_count?: number;
}

interface DirectoryMapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listings: Listing[];
  selectedCity?: string;
  selectedType?: string;
}

const colors = LANDING_COLORS.jams;

export function DirectoryMapModal({
  open,
  onOpenChange,
  listings,
  selectedCity,
}: DirectoryMapModalProps) {
  const mapRef = useRef<MapRef>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const cssReady = useMapboxCss();
  const listingsWithCoords = useMemo(() =>
    listings.filter(l => l.latitude && l.longitude),
    [listings]
  );

  const initialViewState = useMemo(() => {
    if (listingsWithCoords.length === 0) {
      return { latitude: 40.4168, longitude: -3.7038, zoom: 5.5 };
    }

    const promotedListings = listingsWithCoords.filter(l => l.is_promoted);
    if (promotedListings.length > 0) {
      const lats = promotedListings.map(l => l.latitude!);
      const lngs = promotedListings.map(l => l.longitude!);
      const avgLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const avgLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const maxDiff = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));

      let zoom = 10;
      if (maxDiff >= 0.1) zoom = 8;
      else if (maxDiff >= 0.05) zoom = 9;
      else if (maxDiff >= 0.01) zoom = 9.5;

      return { latitude: avgLat, longitude: avgLng, zoom };
    }

    if (selectedCity?.toLowerCase() === 'madrid') {
      return { latitude: 40.4168, longitude: -3.7038, zoom: 9 };
    }

    const avgLat = listingsWithCoords.reduce((sum, l) => sum + (l.latitude || 0), 0) / listingsWithCoords.length;
    const avgLng = listingsWithCoords.reduce((sum, l) => sum + (l.longitude || 0), 0) / listingsWithCoords.length;
    return {
      latitude: avgLat,
      longitude: avgLng,
      zoom: listingsWithCoords.length === 1 ? 12 : 9,
    };
  }, [listingsWithCoords, selectedCity]);

  const handleMarkerClick = useCallback((listing: Listing) => {
    setSelectedListing(listing);
    mapRef.current?.flyTo({
      center: [listing.longitude!, listing.latitude! + 0.002],
      zoom: 15,
      duration: 800,
    });
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setSelectedListing(null);
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onOpenChange(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  if (!open || !cssReady) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        isolation: 'isolate',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={() => onOpenChange(false)}
        style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)' }}
      />

      {/* Map container */}
      <div
        style={{
          position: 'absolute',
          top: 16, left: 16, right: 16, bottom: 16,
          backgroundColor: 'white',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        <Map
          ref={mapRef}
          initialViewState={initialViewState}
          onClick={() => setSelectedListing(null)}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
          mapLib={mapboxgl}
        >
          <NavigationControl position="bottom-right" showCompass={false} />

          {listingsWithCoords.map(listing => {
            const typeColor = TYPE_COLORS[listing.type] || colors.primary;
            const Icon = TYPE_ICONS[listing.type] || MapPin;
            const isSelected = selectedListing?.id === listing.id;

            let zIndex = 1;
            if (isSelected) {
              zIndex = 1000;
            } else if (listing.is_promoted && (listing as any).promotion_order) {
              zIndex = 800 - ((listing as any).promotion_order * 100);
            } else if (listing.is_promoted) {
              zIndex = 500;
            }

            return (
              <Marker
                key={listing.id}
                latitude={listing.latitude!}
                longitude={listing.longitude!}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  handleMarkerClick(listing);
                }}
                style={{ zIndex }}
              >
                <div className="cursor-pointer relative hover:scale-110 active:scale-95 transition-transform duration-150">
                  {listing.is_promoted && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center shadow-sm z-10">
                      <Star className="w-2.5 h-2.5 text-white fill-white" />
                    </div>
                  )}

                  <div
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full shadow-lg border-2 transition-all ${
                      isSelected
                        ? 'bg-white border-gray-900 scale-110'
                        : listing.is_promoted
                          ? 'bg-amber-50 border-amber-400 hover:border-amber-500'
                          : 'bg-white border-white hover:border-gray-200'
                    }`}
                    style={{
                      boxShadow: listing.is_promoted && !isSelected
                        ? '0 4px 12px -2px rgba(245, 158, 11, 0.4)'
                        : undefined
                    }}
                  >
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: typeColor }}
                    >
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[10px] font-bold text-gray-900 max-w-[100px] truncate">
                      {listing.name}
                    </span>
                  </div>
                  <div
                    className={`w-2.5 h-2.5 rotate-45 mx-auto -mt-1 border-r-2 border-b-2 ${
                      isSelected
                        ? 'bg-white border-gray-900'
                        : listing.is_promoted
                          ? 'bg-amber-50 border-amber-400'
                          : 'bg-white border-white'
                    }`}
                  />
                </div>
              </Marker>
            );
          })}

          <AnimatePresence>
            {selectedListing && (
              <Popup
                latitude={selectedListing.latitude!}
                longitude={selectedListing.longitude!}
                offset={25}
                closeButton={false}
                closeOnClick={false}
                anchor="bottom"
                maxWidth="260px"
              >
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                >
                  <Link
                    href={`/directorio/${selectedListing.slug}`}
                    className="block bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100 hover:border-gray-200 transition-colors"
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: `${TYPE_COLORS[selectedListing.type]}15`,
                            color: TYPE_COLORS[selectedListing.type]
                          }}
                        >
                          {TYPE_LABELS[selectedListing.type]}
                        </span>
                        {selectedListing.rating && (
                          <span className="flex items-center gap-0.5 text-[10px]">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" />
                            <span className="font-bold">{selectedListing.rating}</span>
                            {selectedListing.review_count && (
                              <span className="text-gray-400">({selectedListing.review_count})</span>
                            )}
                          </span>
                        )}
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm truncate mb-1">
                        {selectedListing.name}
                      </h4>
                      <p className="text-[10px] text-gray-500 flex items-center gap-1 mb-2">
                        <MapPin className="w-2.5 h-2.5" />
                        {selectedListing.city}{selectedListing.province ? `, ${selectedListing.province}` : ''}
                      </p>
                      <span
                        className="inline-flex items-center gap-1 text-xs font-semibold"
                        style={{ color: colors.primary }}
                      >
                        Ver perfil completo
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              </Popup>
            )}
          </AnimatePresence>
        </Map>
      </div>

      {/* Close button */}
      <button
        onClick={() => onOpenChange(false)}
        className="absolute top-7 right-7 w-10 h-10 bg-white rounded-xl border border-gray-200 shadow-md flex items-center justify-center hover:bg-gray-50 hover:scale-105 transition-all cursor-pointer"
      >
        <X className="w-[18px] h-[18px] text-gray-700" />
      </button>

      {/* Counter badge */}
      <div className="absolute top-7 left-7 bg-white px-4 py-2.5 rounded-xl shadow-md border border-gray-200 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.primary }} />
        <span className="text-[13px] font-semibold text-gray-700">
          {listingsWithCoords.length} establecimientos
        </span>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default DirectoryMapModal;
